import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Paper, Button, CircularProgress, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useScholarships } from '../contexts/ScholarshipsContext';

const ScholarshipDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { scholarships, loading, error } = useScholarships();

  // Try to get schoolId from location.state if available
  const schoolId = location.state?.schoolId;

  // Find the scholarship by id (and optionally schoolId)
  const scholarship = React.useMemo(() => {
    if (!id) return null;
    if (schoolId) {
      return scholarships.find(s => s.id === id && s.schoolId === schoolId) || null;
    }
    return scholarships.find(s => s.id === id) || null;
  }, [id, schoolId, scholarships]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}><CircularProgress /></Box>;
  }
  if (error || !scholarship) {
    return <Alert severity="error">{error || 'Scholarship not found.'}</Alert>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, width: '100%' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'left' }}>{scholarship.name}</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4 }}>
          {/* First Column */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Description:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.description}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Deadline:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.deadline}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Value:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.amount || scholarship.value}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Type:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.type}</Typography>
            </Box>
          </Box>
          {/* Second Column */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Min Avg:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.prereqAvgMin}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Max Avg:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.prereqAvgMax}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Gender:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.gender}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Citizenship:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.citizenship}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'left' }}>Recurring:</Typography>
              <Typography variant="body1" sx={{ textAlign: 'left' }}>{scholarship.recurring}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ScholarshipDetail; 